import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useSEO } from '../../hooks/useSEO';
import { Filter, Target, TrendingUp, ShieldCheck, ArrowRight } from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../ui/accordion';

const features = [
  { icon: Filter, title: 'Onboarding Selectivo', desc: 'Solo profesionales con trayectoria contrastada y capacidad real de generar impacto.' },
  { icon: Target, title: 'Mandatos Relevantes', desc: 'Menos ruido. Más calidad. Más alineación.' },
  { icon: TrendingUp, title: 'Posicionamiento Profesional', desc: 'Forme parte de una red verificada de experiencia senior.' },
  { icon: ShieldCheck, title: 'Protección Total', desc: 'Confidencialidad y control sobre cada oportunidad.' },
];

const faqs = [
  { q: '¿Cómo se valida mi perfil?', a: 'Evaluamos trayectoria, experiencia ejecutiva y capacidad de aportar criterio real. El proceso es selectivo y personalizado.' },
  { q: '¿Qué tipo de mandatos recibiré?', a: 'Solo mandatos alineados con su perfil: finanzas, operaciones, crecimiento, gobernanza, internacionalización, transformación, sucesión o conocimiento sectorial.' },
  { q: '¿Cuál es el modelo económico?', a: 'Freemium: acceso validado y success fee por mandato. Profesional: suscripción mensual con success fee reducido.' },
];

export default function AdvisorPage() {
  const navigate = useNavigate();
  const sectionRef = useScrollAnimation();
  useSEO({
    title: 'Para Profesionales | Conadvy',
    description: 'Ponga su experiencia directiva al servicio de empresas que la necesitan. Acceda a mandatos cualificados y alineados con su perfil.',
    path: '/advisor',
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="advisor-page" className="min-h-screen bg-[#F5F5F3]">
      <Header variant="subpage" />

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-36 md:pb-24 relative overflow-hidden" style={{ '--bg-img-opacity': 1 }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'url(/images/advisors-ejecutivo-ventana.webp)',
            backgroundSize: 'cover',
            backgroundPosition: '81% center',
            opacity: 'var(--bg-img-opacity)',
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(90deg, rgba(248,248,246,0.32), rgba(248,248,246,0.14))' }}
        />
        <div className="text-center relative z-[2]" style={{ maxWidth: '56rem', margin: '0 auto', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="gold-line" style={{ background: '#B9884A' }} />
            <span className="text-[#0C1730] text-xs font-medium tracking-[0.2em] uppercase">Para Profesionales</span>
            <div className="gold-line" style={{ background: '#B9884A' }} />
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-normal tracking-[0.02em] leading-tight mb-6"
            style={{ color: '#0C1730' }}
          >
            Su experiencia merece mandatos a la altura
          </h1>
          <p
            className="text-base md:text-lg font-normal leading-relaxed max-w-2xl mx-auto"
            style={{ color: '#FFFFFF' }}
          >
            Conadvy conecta décadas de experiencia ejecutiva con empresas que
            necesitan criterio real para tomar decisiones importantes.
          </p>
        </div>
      </section>

      {/* Statement */}
      <section className="bg-[#00163E] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00163E] to-[#001F4F]" />
        <div className="grain absolute inset-0" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12">
          <p className="text-white text-2xl md:text-3xl font-normal leading-relaxed mb-6">
            Su experiencia sigue teniendo valor.
          </p>
          <div className="h-[1px] w-12 bg-[#C58C40]/40 mb-6" />
          <p className="text-[#8A9BB1] text-base leading-relaxed max-w-2xl">
            No buscamos currículums. Buscamos criterio, juicio y la capacidad de aportar
            perspectiva donde más se necesita.
          </p>
        </div>
      </section>

      {/* Features */}
      <section ref={sectionRef} className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="space-y-0">
            {features.map((f, i) => (
              <div
                key={f.title}
                data-testid={`advisor-feature-${i}`}
                className={`fade-up fade-up-delay-${Math.min(i + 1, 4)} flex gap-6 py-8 ${
                  i < features.length - 1 ? 'border-b border-[#00163E]/10' : ''
                }`}
              >
                <f.icon size={20} className="text-[#C58C40] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <h3 className="text-[#00163E] text-base font-medium mb-1">{f.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="gold-line" />
            <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase">
              Modelos de Participación
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-[#00163E]/10 p-8 rounded-sm">
              <span className="text-[#C58C40] text-xs font-medium tracking-[0.15em] uppercase block mb-3">Freemium</span>
              <div className="flex items-baseline justify-between mb-4">
                <h4 className="text-[#00163E] text-lg font-medium">Strategic Advisor</h4>
                <span className="text-[#00163E] text-2xl font-light">0 <span className="text-sm text-[#8A9BB1]">€/mes</span></span>
              </div>
              <div className="space-y-2.5 mb-5">
                <p className="text-slate-500 text-sm">Perfil validado</p>
                <p className="text-slate-500 text-sm">OS completo</p>
                <p className="text-slate-500 text-sm">Hasta 5 reuniones/mes</p>
                <p className="text-slate-500 text-sm">Scoring dinámico</p>
              </div>
              <div className="h-[1px] bg-[#00163E]/10 mb-4" />
              <p className="text-[#00163E] text-base font-medium">Success Fee: <span className="text-[#C58C40]">18%</span></p>
            </div>
            <div className="bg-white border border-[#C58C40]/30 p-8 rounded-sm relative">
              <span className="text-[#C58C40] text-xs font-medium tracking-[0.15em] uppercase block mb-3">Professional</span>
              <div className="flex items-baseline justify-between mb-4">
                <h4 className="text-[#00163E] text-lg font-medium">Executive Advisor</h4>
                <span className="text-[#00163E] text-2xl font-light">59 <span className="text-sm text-[#8A9BB1]">€/mes</span></span>
              </div>
              <div className="space-y-2.5 mb-5">
                <p className="text-slate-500 text-sm">Todo lo anterior</p>
                <p className="text-slate-500 text-sm">Reuniones ilimitadas</p>
                <p className="text-slate-500 text-sm">Acceso a Partners</p>
                <p className="text-slate-500 text-sm">Recursos avanzados</p>
              </div>
              <div className="h-[1px] bg-[#00163E]/10 mb-4" />
              <p className="text-[#00163E] text-base font-medium">Success Fee: <span className="text-[#C58C40]">10%</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase block mb-6">
            Preguntas frecuentes
          </span>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#00163E]/10">
                <AccordionTrigger className="faq-trigger text-[#00163E] text-base font-normal py-5 hover:no-underline hover:text-[#C58C40] transition-colors">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-500 text-sm leading-relaxed pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <button
            onClick={() => navigate('/advisor/registro')}
            data-testid="advisor-registro-cta"
            className="inline-flex items-center gap-3 bg-[#C58C40] text-white hover:bg-[#b07b35] transition-colors duration-300 rounded-sm px-10 py-4 text-sm tracking-[0.15em] uppercase group"
          >
            <span>Solicitar Acceso</span>
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          <p className="text-[#8A9BB1] text-xs mt-4">
            Proceso de evaluación confidencial. Solo perfiles verificados.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
